import log from "loglevel";
import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import throttle from "lodash.throttle";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@twilio-paste/core/box";
import { InputBox } from "@twilio-paste/core/input-box";
import { TextArea } from "@twilio-paste/core/textarea";
import { Button } from "@twilio-paste/core/button";
import { SendIcon } from "@twilio-paste/icons/esm/SendIcon";

import { AppState } from "../store/definitions";
import { AttachFileButton } from "./AttachFileButton";
import { FilePreview } from "./FilePreview";
import {
    addNotification,
    detachFiles,
    updateAwaitingBot,
    updateWelcomeInputLock
} from "../store/actions/genericActions";
import { notifications } from "../notifications";
import { reportClientError } from "../utils/reportClientError";
import { classificarEtapa, consultarEstadoReal, extrairCodigos, gerarReportId } from "../utils/diagnosticoDeEnvio";
import { descreverErro } from "../utils/descreverErro";
import { isHumanMode } from "../store/selectors";
import { CHAR_LIMIT, WELCOME_INPUT_LOCK_MS } from "../constants";
import { ACTION_UPDATE_CONVERSATION_STATE } from "../store/actions/actionTypes";
import {
    formStyles,
    innerInputStyles,
    messageOptionContainerStyles,
    filePreviewContainerStyles,
    textAreaContainerStyles,
    inputRowStyles,
    attachButtonContainerStyles,
    inputBoxWrapperStyles
} from "./styles/MessageInput.styles";

export const MessageInput = () => {
    const dispatch = useDispatch();
    const [text, setText] = useState("");
    const [isSending, setIsSending] = useState(false);
    const { conversation, conversationsClient, attachedFiles, fileAttachmentConfig, awaitingBot, welcomeInputLock, humanMode } = useSelector(
        (state: AppState) => ({
            conversation: state.chat.conversation,
            conversationsClient: state.chat.conversationsClient,
            attachedFiles: state.chat.attachedFiles || [],
            fileAttachmentConfig: state.config.fileAttachment,
            awaitingBot: state.chat.awaitingBot,
            welcomeInputLock: state.chat.welcomeInputLock,
            humanMode: isHumanMode(state.chat)
        })
    );
    const oldAttachmentsLength = useRef((attachedFiles || []).length);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const attachmentsBoxRef = useRef<HTMLDivElement>(null);

    const throttleChange = useMemo(
        () =>
            throttle(() => {
                conversation?.typing();

                // in case the input was already focused, let's make sure to send the `read` status if the customer is typing
                if (conversation?.lastReadMessageIndex !== conversation?.lastMessage?.index) {
                    conversation?.setAllMessagesRead();
                }
            }, 500),
        [conversation]
    );

    /*
     * "Cliente digitando" pro atendente (Workspace lê via REST/polling). Só no MODO HUMANO: durante a
     * IA não há atendente e evitamos mexer nos attributes junto do botTyping. Merge sobre os attributes
     * em memória (já trazem handoff/agentTyping do listener). Debounced: liga no keystroke, desliga após
     * 3s parado / no envio. Aditivo em prod (o Flex ignora a chave; o widget nativo segue com typing()).
     */
    const custTypingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const custTypingActive = useRef(false);
    const setCustomerTyping = (typing: boolean) => {
        if (!conversation) return;
        const attrs = (conversation.attributes && typeof conversation.attributes === "object" ? conversation.attributes : {}) as Record<string, unknown>;
        /*
         * BEST-EFFORT BLINDADO (mesmo contrato de `alert.js`/`heartbeat.js`): o balãozinho
         * de "digitando" é cosmético e vive FORA do caminho do usuário — não pode, em
         * hipótese alguma, impedir o cliente de mandar a mensagem.
         *
         * O `.catch` sozinho não bastava: ele cobre a rejeição da promessa, mas
         * `updateAttributes` ausente estoura de forma SÍNCRONA e a exceção subia pelo
         * `send()`. É a mesma forma de falha (método que não existe no objeto) que
         * derrubou o widget em produção no dia 28/07 — agora ela morre aqui.
         */
        try {
            conversation.updateAttributes({ ...attrs, customerTyping: typing })?.catch(() => undefined);
        } catch {
            /* indicador não subiu; o atendimento segue */
        }
    };
    const stopCustomerTyping = () => {
        if (custTypingTimer.current) { clearTimeout(custTypingTimer.current); custTypingTimer.current = null; }
        if (custTypingActive.current) { custTypingActive.current = false; setCustomerTyping(false); }
    };
    const signalCustomerTyping = () => {
        if (!humanMode || !conversation) return;
        if (!custTypingActive.current) { custTypingActive.current = true; setCustomerTyping(true); }
        if (custTypingTimer.current) clearTimeout(custTypingTimer.current);
        custTypingTimer.current = setTimeout(stopCustomerTyping, 3000);
    };
    useEffect(() => () => stopCustomerTyping(), []); // unmount → limpa

    /*
     * Trava fixa do composer ao iniciar a conversa (cobre o load do fluxo de boas-vindas, com
     * botões ou não — timer fixo, não orientado a evento). Libera após WELCOME_INPUT_LOCK_MS;
     * o cleanup limpa o timer se a janela fechar antes (re-agenda no próximo mount, sempre destrava).
     */
    useEffect(() => {
        if (!welcomeInputLock) {
            return undefined;
        }
        const timer = setTimeout(() => dispatch(updateWelcomeInputLock(false)), WELCOME_INPUT_LOCK_MS);
        return () => clearTimeout(timer);
    }, [welcomeInputLock, dispatch]);

    /*
     * Na fase IA, trava o envio enquanto espera o bot (anti-spam) e nos ~3s iniciais (welcomeInputLock).
     * No modo humano NUNCA trava (o cliente tem que escrever pro atendente a qualquer momento).
     */
    const isLocked = isSending || ((Boolean(awaitingBot) || Boolean(welcomeInputLock)) && !humanMode);
    const isSubmitDisabled = (!text.trim() && !attachedFiles?.length) || isLocked;
    // O botão de enviar só aparece quando há texto ou anexo
    const hasContent = Boolean(text.trim()) || Boolean(attachedFiles?.length);

    const send = async () => {
        if (isSubmitDisabled) {
            return;
        }
        if (!conversation) {
            log.error("Failed sending message: no conversation found");
            return;
        }
        setIsSending(true);
        /*
         * Só texto gera resposta da IA (o backend ignora mídia) e só na fase IA. Então:
         * - texto na fase IA → entra em "esperando o bot" na hora (balão + lock);
         * - só arquivo, OU modo humano → NÃO trava; garante o estado solto.
         */
        if (text.trim() && !humanMode) {
            dispatch(updateAwaitingBot(true));
        } else {
            dispatch(updateAwaitingBot(false));
        }

        let preparedMessage = conversation.prepareMessage();
        preparedMessage = preparedMessage.setBody(text);
        attachedFiles.forEach((file: File) => {
            const formData = new FormData();
            formData.append(file.name, file);
            preparedMessage.addMedia(formData);
        });
        // Lado do CSAT: encerra o "digitando" do cliente ANTES do envio — o indicador
        // não pode continuar aceso enquanto a mensagem sobe.
        stopCustomerTyping();
        /*
         * O envio PRECISA de tratamento de erro: sem ele, uma falha de upload deixava
         * `isSending` preso em true e o composer travava de vez — o cliente não conseguia
         * digitar, nem remover o anexo, nem tentar de novo (só recarregando a página).
         * Foi o que a operação relatou no Android, onde o upload falha bem mais.
         *
         * Na falha o que ele escreveu e anexou é PRESERVADO (limpar seria perder o
         * trabalho junto com a mensagem) e o `awaitingBot` volta atrás — senão a espera
         * por uma resposta que nunca virá trava o composer por outro caminho.
         */
        try {
            await preparedMessage.build().send();
            setText("");
            dispatch(detachFiles(attachedFiles));
        } catch (e) {
            log.error(`Failed sending message: ${descreverErro(e)}`);
            dispatch(updateAwaitingBot(false));
            /*
             * 400 do Twilio = a conversa está `closed`. NÃO é falha de conexão, e tratar como se
             * fosse manda o apostador investigar a internet dele enquanto o problema é que o
             * atendimento terminou — foi o erro mais frequente em produção (26–28/08).
             * Causa-raiz reproduzida ao vivo: `inactive` envia normalmente; só `closed` recusa.
             *
             * Marcar o estado encerrado leva o cliente à MESMA tela de despedida do encerramento
             * normal (com CSAT, se houver) em vez de deixá-lo tentando de novo pra sempre.
             */
            /*
             * ⚠️ SÓ vale SEM ANEXO (revisão externa, 2026-08-30). O `send()` acima FAZ o upload da
             * mídia, e arquivo inválido ou grande demais devolve 400 igual. Sem esta condição, uma
             * conversa ATIVA era marcada como encerrada e o apostador ia parar na tela de despedida
             * por causa de uma foto que não subiu — e o SDD do teste de falha de envio registra que
             * upload falhando no Android é justamente o caso frequente.
             *
             * Com anexo o 400 é ambíguo, então cai no tratamento comum abaixo, que PRESERVA texto e
             * arquivo para o cliente tentar de novo. Na dúvida, o erro seguro é "não consegui
             * enviar", nunca "seu atendimento acabou".
             */
            if ((e as { status?: number })?.status === 400 && attachedFiles.length === 0) {
                dispatch({
                    type: ACTION_UPDATE_CONVERSATION_STATE,
                    payload: { conversationState: "closed", conversationSid: conversation?.sid }
                });
                dispatch(addNotification(notifications.conversationEndedNotification()));
                // evento PRÓPRIO: não é falha de rede, e misturar os dois cega a operação
                reportClientError("send_failed_conversation_ended", {
                    reason: descreverErro(e),
                    ...extrairCodigos(e)
                });
                return;
            }
            dispatch(addNotification(notifications.failedToSendMessageNotification()));
            /*
             * Sem este aviso o erro morre no console do celular do cliente e a operação
             * só descobre pelo relato dele. Vai o motivo técnico + tipo/tamanho da mídia —
             * nunca o nome do arquivo nem o texto da mensagem.
             */
            /*
             * TODOS os anexos, não só o primeiro: a falha pode ter sido no 2º arquivo, e reportar só
             * o 1º manda a operação investigar o arquivo errado. Vão tipo, contagem e o tamanho
             * SOMADO — jamais o NOME (print de apostador se chama "CPF_Fulano.jpg" o tempo todo).
             */
            const midia = attachedFiles.length
                ? {
                      // Deduplicado: tipo repetido não informa nada e ainda estoura o limite do
                      // alerta (6 PDFs iguais viravam 100 chars, cortados no meio). O QUANTOS é o
                      // `mediaCount`; o `mediaType` responde QUAIS.
                      mediaType: Array.from(new Set(attachedFiles.map((f) => f.type || "desconhecido"))).join(", "),
                      mediaSize: attachedFiles.reduce((soma, f) => soma + (f.size || 0), 0),
                      mediaCount: attachedFiles.length
                  }
                : undefined;
            /*
             * 🔒 DIAGNÓSTICO, não decisão de tela: nada daqui muda o que o apostador vê. O aviso e a
             * liberação do composer já aconteceram acima; isto só enriquece o alerta da operação.
             *
             * A consulta ao estado real tem timeout curto e NUNCA lança (`consultarEstadoReal`).
             * Ela distingue "conversa ativa" de "não consegui verificar" — colapsar os dois faria
             * "não sei" passar por "está ativa", que foi o defeito apontado na revisão.
             *
             * `void`: telemetria é fire-and-forget e não pode segurar quem está falando com o
             * suporte. Se o apostador fechar a página durante a consulta, este alerta se perde — é
             * o custo aceito por não atrasar o atendimento.
             */
            void consultarEstadoReal(conversationsClient, conversation?.sid).then((consulta) =>
                reportClientError("send_failed", {
                    reason: descreverErro(e),
                    reportId: gerarReportId(),
                    ...(classificarEtapa(e) ? { failureStage: classificarEtapa(e) } : {}),
                    ...extrairCodigos(e),
                    ...(conversation?.state?.current
                        ? { conversationStateLocal: conversation.state.current as "active" | "inactive" | "closed" }
                        : {}),
                    ...consulta,
                    humanMode,
                    ...(conversationsClient?.connectionState
                        ? { connectionState: String(conversationsClient.connectionState) }
                        : {}),
                    ...(midia || {})
                })
            );
        } finally {
            setIsSending(false);
            textAreaRef.current?.focus();
        }
    };

    const onKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            (e.target as HTMLInputElement).form?.dispatchEvent(
                new Event("submit", { cancelable: true, bubbles: true })
            );
        }
    };

    const onChange = (val: ChangeEvent<HTMLTextAreaElement>) => {
        setText(val.target.value);

        throttleChange();
        if (val.target.value.trim()) signalCustomerTyping(); else stopCustomerTyping();
    };

    const onFocus = () => {
        conversation?.setAllMessagesRead();
    };

    useEffect(() => {
        textAreaRef.current?.setAttribute("rows", "1");
        textAreaRef.current?.focus();
    }, [textAreaRef]);

    // Ensuring attached files are automatically scrolled to
    useEffect(() => {
        if (!attachmentsBoxRef.current) {
            return;
        }

        if (attachedFiles.length > oldAttachmentsLength.current) {
            (attachmentsBoxRef.current.lastChild as Element)?.scrollIntoView();
        }

        oldAttachmentsLength.current = attachedFiles.length;
    }, [attachedFiles]);

    return (
        <Box
            as="form"
            {...formStyles}
            onSubmit={(e) => {
                e.preventDefault();
                send();
            }}
        >
            {attachedFiles && attachedFiles.length > 0 && (
                <Box data-test="message-attachments" {...filePreviewContainerStyles} ref={attachmentsBoxRef}>
                    {attachedFiles.map((file, index) => (
                        <FilePreview focusable={true} key={index} file={file} isBubble={false} disabled={isSending} />
                    ))}
                </Box>
            )}
            <Box {...inputRowStyles}>
                {fileAttachmentConfig?.enabled && (
                    <Box {...attachButtonContainerStyles}>
                        <AttachFileButton textAreaRef={textAreaRef} />
                    </Box>
                )}
                <Box {...inputBoxWrapperStyles}>
                    <InputBox element="MESSAGE_INPUT_BOX" disabled={isLocked}>
                        <Box as="div" {...innerInputStyles}>
                            <Box {...textAreaContainerStyles}>
                                <TextArea
                                    ref={textAreaRef}
                                    data-test="message-input-textarea"
                                    placeholder="Escreva sua mensagem..."
                                    value={text}
                                    element="MESSAGE_INPUT"
                                    onChange={onChange}
                                    onFocus={onFocus}
                                    readOnly={isLocked}
                                    onKeyPress={onKeyPress}
                                    maxLength={CHAR_LIMIT}
                                />
                            </Box>
                            <Box {...messageOptionContainerStyles} style={{ display: hasContent ? "flex" : "none" }}>
                                <Button
                                    data-test="message-send-button"
                                    variant="primary_icon"
                                    size="icon_small"
                                    type="submit"
                                    aria-disabled={isSubmitDisabled}
                                >
                                    <SendIcon decorative={false} title="Enviar mensagem" size="sizeIcon30" />
                                </Button>
                            </Box>
                        </Box>
                    </InputBox>
                </Box>
            </Box>
        </Box>
    );
};
